import React, { useEffect, useRef, useState } from 'react';
import { ButtonsDiv, Divider, FormRow, InputField, SignUpForm } from './styles';
import InputPassword from 'common/InputPassword/InputPassword';
import Button from 'components/Buttom/Button';
import FieldMask from 'components/FieldMask';
import { Formik, Field, Form, FormikProps, FormikValues } from 'formik';
import SelectComponent from 'components/Select';
import { states, StatesEnum, validateCPF } from '../../utils';
import ErrorMessage from 'components/ErrorMessage';
import yup from 'utils/validation';
import { validDDDs } from 'utils/basicUtils';
import FieldCEPComponent from 'components/FieldCEPComponent';
import SelectCityComponent from 'components/SelectCityComponent';
import { CIDADES_POR_ESTADO, Estados } from 'utils/location';
import MaskedPhoneInput from 'features/Config/components/MaskedPhone/MaskedPhoneInput';

const validationSchema = yup.object({
  document: yup
    .string()
    .matches(/^([\w\.\-\/]{18}|[\w\.\-\/]{14})$/, 'Informe um CPF completo')
    .test('is-valid-cpf', 'Informe um CPF válido', value => validateCPF(value || ''))
    .trim()
    .required(),
  name: yup
    .string()
    .matches(/^(?!^\d+$)[\d\D]*$/, 'Este campo não pode conter somente números')
    .validateFullName()
    .min(3)
    .trim()
    .required(),
  email: yup
    .string()
    .email()
    .matches(/^(?!\d+$).*/, 'O campo email não pode ser apenas números')
    .trim()
    .required(),
  password: yup.string().min(8).trim().required(),
  confirm_password: yup
    .string()
    .min(8)
    .oneOf([yup.ref('password'), undefined], 'As senhas não coincidem')
    .trim()
    .required(),
  extraInfo: yup.object({
    phone: yup
      .string()
      .required('Telefone é obrigatório')
      .matches(
        /^(\(\d{2}\)\s\d\s\d{4}-\d{4}|\(\d{2}\)\s\d{4}-\d{4})$/,
        'Telefone deve ter o formato (XX) X XXXX-XXXX ou (XX) XXXX-XXXX'
      )
      .test('is-valid-ddd', 'DDD inválido', function (value) {
        if (!value) return false;
        const ddd = value.match(/^\((\d{2})\)/);
        if (!ddd) return false;
        return validDDDs.includes(ddd[1]);
      })
      .test('not-all-same', 'Telefone não pode ter todos os números iguais', function (value) {
        if (!value) return false;
        const digits = value.replace(/\D/g, '');
        return !/^(\d)\1+$/.test(digits);
      })
      .trim(),
    address: yup.object({
      additionalData: yup.string().trim(),
      city: yup
        .string()
        .matches(/^(?!^\d+$)[\d\D]*$/, 'A cidade não pode conter somente números')
        .trim()
        .required(),
      neighborhood: yup
        .string()
        .matches(/^(?!^\d+$)[\d\D]*$/, 'O bairro não pode conter somente números')
        .trim()
        .required(),
      number: yup
        .string()
        .required()
        // .matches(/^\d+$/, 'Número deve conter apenas dígitos')
        .trim('Número não pode ter espaços vazios'),
      state: yup.string().required().trim(),
      street: yup
        .string()
        .matches(/^(?!\d+$).*/, 'O campo endereço não pode ser apenas números')
        .trim()
        .required(),
      zipCode: yup
        .string()
        // .matches(/^\d{5}-?\d{3}$/, 'CEP deve ter o formato XXXXX-XXX')
        .test('is-not-equal', 'CEP não pode conter todos os números iguais', function (value) {
          if (!value) return false;
          const cleanedValue = value.replace('-', '');
          return !/^(.)\1+$/.test(cleanedValue);
        })
        .trim()
        .required(),
    }),
  }),
});

const initialValues = {
  name: '',
  email: '',
  document: '',
  password: '',
  type: 'tour-pf',
  extraInfo: {
    role: 'tour-pf',
    cadastur: '',
    legalName: '',
    responsibleName: '',
    responsibleCpf: '',
    phone: '',
    tradeName: '',
    address: {
      additionalData: '',
      city: '',
      neighborhood: '',
      number: '',
      state: '',
      street: '',
      zipCode: '',
    },
  },
};

interface Props {
  onCancel: () => void;
  onSubmit: (values: FormikValues) => void;
}

const PfForm = (props: Props): JSX.Element => {
  const pfForm = useRef<FormikProps<FormikValues>>(null);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [cidades, setCidades] = useState<{ label: string; value: string }[]>([]);
  const [selectedState, setSelectedState] = useState<StatesEnum | ''>('');
  const { values, setFieldValue } = pfForm.current || {};

  const handleStateChange = async (estado: string) => {
    const cidadesEncontradas = CIDADES_POR_ESTADO[estado as Estados] || [];
    setCidades(
      cidadesEncontradas
        .map(cidade => ({
          label: cidade.nomeIBGE,
          value: cidade.nomeIBGE,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  };

  useEffect(() => {
    setSelectedState(pfForm?.current?.values?.extraInfo.address.state);
    handleStateChange(pfForm?.current?.values?.extraInfo.address.state);
    if (pfForm?.current?.values?.extraInfo.address.city) {
      const cidadesEncontradas = CIDADES_POR_ESTADO[pfForm?.current?.values.extraInfo.address.state as Estados] || [];
      const cidadeExiste = cidadesEncontradas.some(cidade => cidade.nomeIBGE === values?.extraInfo.address.city);
      if (!cidadeExiste && setFieldValue) {
        setFieldValue('extraInfo.address.city', '');
      }
    }
  }, [pfForm?.current?.values?.extraInfo.address.state]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      pfForm.current?.submitForm();
      if (pfForm.current?.values) {
        props.onSubmit(pfForm.current.values);
      }
    }
  };

  return (
    <Formik
      enableReinitialize
      innerRef={pfForm}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={props.onSubmit}>
      <SignUpForm>
        <header>
          <h2>Dados do responsável:</h2>
          <span>2/2</span>
        </header>
        <Form onKeyDown={handleKeyDown}>
          <FormRow>
            <InputField id="cpf-input">
              <div>
                <label htmlFor="">
                  CPF<span>*</span>
                </label>
                <FieldMask type="cpf">
                  <Field type="text" name="document" placeholder="ex: xxx.xxx.xxx-xx" />
                </FieldMask>
              </div>
              <ErrorMessage name="document" />
            </InputField>

            <InputField id="full-name-input">
              <div>
                <label htmlFor="">
                  Nome Completo<span>*</span>
                </label>
                <Field name="name" />
              </div>
              <ErrorMessage name="name" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="cellphone-input">
              <div>
                <label htmlFor="">
                  Telefone ou Celular<span>*</span>
                </label>
                <FieldMask type="phone">
                  <Field name="extraInfo.phone" component={MaskedPhoneInput} />
                </FieldMask>
              </div>
              <ErrorMessage name="extraInfo.phone" />
            </InputField>
            <InputField id="email-input">
              <div>
                <label htmlFor="">
                  E-mail<span>*</span>
                </label>
                <FieldMask type="email">
                  <Field type="text" name="email" />
                </FieldMask>
              </div>
              <ErrorMessage name="email" />
            </InputField>
            <InputField id="cep-input">
              <div>
                <FieldCEPComponent
                  name="extraInfo.address.zipCode"
                  currentCity={pfForm.current?.values?.extraInfo?.address?.city}
                  label="CEP"
                  required
                  placeholder="ex: xxxxx-xxx"
                  value={pfForm.current?.values?.extraInfo.address.zipCode}
                  onChange={(field, value) => {
                    pfForm.current?.setFieldValue(field, value);
                    setSelectedState(pfForm?.current?.values.extraInfo.address.state);
                  }}
                  fieldMappings={[
                    {
                      formField: 'extraInfo.address.street',
                      responseField: 'logradouro',
                    },
                    {
                      formField: 'extraInfo.address.neighborhood',
                      responseField: 'bairro',
                    },
                    {
                      formField: 'extraInfo.address.city',
                      responseField: 'localidade',
                    },
                    {
                      formField: 'extraInfo.address.state',
                      responseField: 'uf',
                    },
                  ]}
                  nextFieldRef={numberInputRef}
                  fallbackFieldRef={addressInputRef}
                />
              </div>
              <ErrorMessage name="extraInfo.address.zipCode" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="address-input">
              <div>
                <label htmlFor="">
                  Endereço<span>*</span>
                </label>
                <Field innerRef={addressInputRef} name="extraInfo.address.street" />
              </div>
              <ErrorMessage name="extraInfo.address.street" />
            </InputField>
            <InputField id="number-input">
              <div>
                <label htmlFor="">
                  Número<span>*</span>
                </label>
                <Field innerRef={numberInputRef} name="extraInfo.address.number" />
              </div>
              <ErrorMessage name="extraInfo.address.number" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="complement-input">
              <div>
                <label htmlFor="">Complemento</label>
                <Field name="extraInfo.address.additionalData" />
              </div>
              <ErrorMessage name="extraInfo.address.additionalData" />
            </InputField>
            <InputField id="neighborhood-input">
              <div>
                <label htmlFor="">
                  Bairro<span>*</span>
                </label>
                <Field name="extraInfo.address.neighborhood" />
              </div>
              <ErrorMessage name="extraInfo.address.neighborhood" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="state-input">
              <div>
                <Field
                  validateOnBlur
                  component={({ field, form }: { field: any; form: any }) => (
                    <SelectComponent
                      label="Estado"
                      required
                      options={states.map(state => ({ label: state.label, value: state.value }))}
                      onChange={value => {
                        form.setFieldValue('extraInfo.address.state', value);
                        setSelectedState(value as StatesEnum);
                        handleStateChange(value as string); // Atualiza o estado e carrega as cidades
                      }}
                      name="extraInfo.address.state"
                      selected={field.value ? [field.value] : []} // Mantém o estado selecionado
                      //disabled={disabledInput}
                    />
                  )}
                  name="extraInfo.address.state"
                />
              </div>
              <ErrorMessage name="extraInfo.address.state" />
            </InputField>
            <InputField id="city-input">
              <div>
                <SelectCityComponent
                  label="Cidade"
                  name="extraInfo.address.city"
                  value={
                    cidades.find(cidade => cidade.value === pfForm.current?.values?.extraInfo.address.city)?.value || ''
                  }
                  options={cidades}
                  onChange={(selectedValue: string) => {
                    pfForm.current?.setFieldValue('extraInfo.address.city', selectedValue);
                  }}
                  onBlur={() => pfForm.current?.setFieldTouched('extraInfo.address.city', true)}
                  placeholderText="Selecione a cidade"
                  required
                />
              </div>
              <ErrorMessage name="extraInfo.address.city" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="password-input">
              <div>
                <label htmlFor="">
                  Senha<span>*</span>
                </label>
                <InputPassword name={'password'} />
              </div>
              <ErrorMessage name="password" />
            </InputField>
            <InputField id="password-input">
              <div>
                <label htmlFor="">
                  Confirmação de Senha<span>*</span>
                </label>
                <InputPassword name={'confirm_password'} />
              </div>
              <ErrorMessage name="confirm_password" />
            </InputField>
          </FormRow>
          <Divider />
          <ButtonsDiv>
            <Button onClick={props.onCancel} text="VOLTAR" />
            <Button
              onClick={() => {
                props.onCancel();
                pfForm?.current?.setValues(initialValues);
              }}
              text="CANCELAR"
            />
            <Button text="CRIAR" type="submit" />
          </ButtonsDiv>
        </Form>
      </SignUpForm>
    </Formik>
  );
};

export default PfForm;
